import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
interface PlayerSummaryCardProps {
  playerNumber: number;
}
export function PlayerSummaryCard({
  playerNumber
}: PlayerSummaryCardProps) {
  const [playerName, setPlayerName] = useState('Player');
  const [netWorth, setNetWorth] = useState(0);
  const [cashFlow, setCashFlow] = useState(0);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  // Load data from localStorage
  const loadPlayerData = () => {
    const savedData = localStorage.getItem('playerData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setPlayerName(parsedData.playerName || 'Player');
        setMonthlyIncome(parsedData.totalIncome || 0);
        setMonthlyExpenses(parsedData.totalExpenses || 0);
        // Calculate derived values
        const totalAssets = parsedData.totalAssets || 0;
        const totalLiabilities = parsedData.totalLiabilities || 0;
        setNetWorth(totalAssets - totalLiabilities);
        setCashFlow((parsedData.totalIncome || 0) - (parsedData.totalExpenses || 0));
        setLastUpdated(Date.now());
      } catch (error) {
        console.error('Error loading player data:', error);
      }
    }
  };
  // Initial load and set up event listeners
  useEffect(() => {
    // Initial load
    loadPlayerData();
    // Set up event listener for storage changes (for cross-tab updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'playerData') {
        loadPlayerData();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    // Custom event for same-tab updates
    const handleCustomEvent = () => {
      loadPlayerData();
    };
    // Listen for the custom event
    window.addEventListener('playerUpdated', handleCustomEvent);
    // Set up an interval to periodically check for updates
    const intervalId = setInterval(loadPlayerData, 2000);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('playerUpdated', handleCustomEvent);
      clearInterval(intervalId);
    };
  }, []);
  return <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100" data-id="element-175">
      <div className="flex justify-between items-center mb-3" data-id="element-176">
        <h2 className="text-lg font-semibold text-gray-800" data-id="element-177">
          {playerName}'s Financial Summary
        </h2>
        <Link to="/cashflow" className="text-green-600 hover:text-green-700 flex items-center text-sm font-medium" data-id="element-178">
          View Details
          <ArrowRight size={16} className="ml-1" data-id="element-179" />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3" data-id="element-180">
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-181">
          <p className="text-xs text-gray-500" data-id="element-182">Net Worth</p>
          <p className="text-lg font-bold text-green-600" data-id="element-183">
            ${netWorth.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-184">
          <p className="text-xs text-gray-500" data-id="element-185">Cash Flow</p>
          <p className="text-lg font-bold text-green-600" data-id="element-186">
            ${cashFlow.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-187">
          <p className="text-xs text-gray-500" data-id="element-188">Monthly Income</p>
          <p className="text-lg font-bold text-gray-800" data-id="element-189">
            ${monthlyIncome.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-190">
          <p className="text-xs text-gray-500" data-id="element-191">Monthly Expenses</p>
          <p className="text-lg font-bold text-gray-800" data-id="element-192">
            ${monthlyExpenses.toLocaleString()}
          </p>
        </div>
      </div>
    </div>;
}